import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, TrendingUp, Users, Coins, ArrowDownToLine, ArrowUpFromLine, LogOut, Gem, ScrollText, Crown, Wallet as WalletIcon, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/investments", label: "Investments", icon: TrendingUp },
  { to: "/portal/plans", label: "Plans", icon: ScrollText },
  { to: "/portal/rewards", label: "Rewards", icon: Crown },
  { to: "/portal/team", label: "My Team", icon: Users },
  { to: "/portal/income", label: "Income", icon: Coins },
  { to: "/portal/deposit", label: "Deposit", icon: ArrowDownToLine },
  { to: "/portal/wallet", label: "Wallet", icon: WalletIcon },
  { to: "/portal/withdraw", label: "Withdraw", icon: ArrowUpFromLine },
];

function NavItems({ onNavigate }) {
  return (
    <>
      {nav.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={onNavigate}
          data-testid={`nav-${n.label.toLowerCase().replace(' ', '-')}`}
          className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${isActive ? "gold-btn" : "text-slate-400 hover:text-amber-300 hover:bg-amber-500/5"}`}>
          <n.icon className="w-5 h-5" /> {n.label}
        </NavLink>
      ))}
    </>
  );
}

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#050507] flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 border-r border-amber-500/15 bg-[#08080C] fixed inset-y-0 z-30">
        <div className="h-20 flex items-center gap-2 px-6 border-b border-amber-500/15">
          <div className="w-9 h-9 rounded-xl gold-btn grid place-items-center"><Gem className="w-5 h-5 text-black" /></div>
          <div className="font-display font-extrabold">Money<span className="gold-text">MoonX</span></div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto"><NavItems /></nav>
        <div className="p-4 border-t border-amber-500/15">
          <button data-testid="logout-btn" onClick={doLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/5 w-full transition-all">
            <LogOut className="w-5 h-5" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 inset-y-0 w-72 max-w-[80%] bg-[#08080C] border-r border-amber-500/20 flex flex-col animate-in slide-in-from-left duration-200">
            <div className="h-16 flex items-center justify-between px-5 border-b border-amber-500/15">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg gold-btn grid place-items-center"><Gem className="w-4 h-4 text-black" /></div>
                <div className="font-display font-extrabold text-sm">Money<span className="gold-text">MoonX</span></div>
              </div>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="text-slate-400 hover:text-amber-300 p-1"><X className="w-6 h-6" /></button>
            </div>
            <nav className="flex-1 p-4 space-y-1 overflow-y-auto"><NavItems onNavigate={() => setOpen(false)} /></nav>
            <div className="p-4 border-t border-amber-500/15">
              <button data-testid="mobile-logout-btn" onClick={doLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/5 w-full">
                <LogOut className="w-5 h-5" /> Logout
              </button>
            </div>
          </aside>
        </div>
      )}

      <div className="flex-1 md:ml-64 min-w-0">
        <header className="h-16 md:h-20 glass border-b border-amber-500/15 flex items-center justify-between gap-3 px-4 sm:px-6 sticky top-0 z-40">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)}
              className="md:hidden w-10 h-10 grid place-items-center rounded-xl border border-amber-500/25 text-amber-300 shrink-0">
              <Menu className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <div className="text-xs text-slate-400 hidden sm:block">Investor Portal</div>
              <div className="font-display font-bold text-base md:text-lg truncate">{user?.name}</div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[10px] md:text-xs text-slate-500 uppercase tracking-wide">Referral Code</div>
            <div className="font-mono-fin text-amber-400 font-bold text-sm md:text-base">{user?.referral_code}</div>
          </div>
        </header>

        <main className="p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
